import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import { Check } from "lucide-react";
import { CTASection } from "@/components/cta-section";
import { InternalLinksHub } from "@/components/internal-links-hub";
import { SeoFAQ } from "@/components/seo-faq";
import {
  buildHead,
  breadcrumbSchema,
  faqSchema,
  serviceSchema,
  ensureFAQs,
  type FAQ,
} from "@/lib/seo";

const PRIMARY_KEYWORD = "hr packages for small businesses";

const META_TITLE = "HR Packages for Small Businesses | Plans & Pricing";
const META_DESCRIPTION =
  "HR packages for small businesses from £199 a month. Transparent UK HR support packages and employment law cover, named advisor, no long tie-in.";

const customFaqs: FAQ[] = [
  {
    question: "What is included in HR packages for small businesses from EmployerHQ?",
    answer:
      "HR packages for small businesses from EmployerHQ bundle the day-to-day support a UK SME actually needs into a single fixed monthly fee. " +
      "Every hr package includes a named CIPD-qualified advisor, unlimited phone and email advice on UK employment law, a bespoke contract of employment, a full handbook of policies aligned to the Employment Rights Act 1996 and the Equality Act 2010, and hands-on support with disciplinary, grievance, absence and performance cases. " +
      "Our Growth and Enterprise hr support packages add solicitor-led legal advice, tribunal preparation and defence, manager training and quarterly strategic reviews. " +
      "Because these are hr retainer packages rather than ad-hoc hourly work, you get a predictable cost, faster response times and an advisor who already knows your business when something goes wrong.",
  },
  {
    question: "How much do HR packages for small businesses cost in the UK?",
    answer:
      "HR packages for small businesses in the UK typically range from around £150 to £600 per month for a team of up to 50 people, depending on what is included. " +
      "Our hr service packages start at £199 per month for the Essentials plan (up to 25 employees), £449 per month for Growth (25–100 employees) and a custom quote for Enterprise (100+ employees, group companies or multi-site operations). " +
      "Compared with hiring a part-time in-house HR manager (£20,000–£35,000 plus on-costs) or paying employment solicitors at £250–£450 per hour, an hr outsourcing package usually saves a UK SME between 40% and 70% of its annual HR spend, while giving access to a wider team of specialists.",
  },
  {
    question: "Do your HR packages for small businesses include employment law advice?",
    answer:
      "Yes. Every one of our hr packages for small businesses includes a full employment law package as standard, not as an upsell. " +
      "Your named advisor will give you legally-grounded guidance on contracts, dismissals, redundancies, TUPE, settlement agreements, discrimination, working time, family leave and pay disputes. " +
      "From the Growth plan upwards, your hr consultancy package also includes solicitor-led advice and full tribunal preparation and defence, so you do not need a separate law firm on retainer. " +
      "All advice is delivered in plain English, in writing where you need a record, and benchmarked against current ACAS Codes of Practice so you have something defensible if a case ever reaches the Employment Tribunal.",
  },
  {
    question: "Can I change or cancel my HR package as my small business grows?",
    answer:
      "Yes. All EmployerHQ hr packages for small businesses are monthly rolling, with 30 days' notice to cancel or change plan, so you are never locked into a multi-year deal. " +
      "If your headcount moves through the bands (for example crossing 25 or 100 employees), we will simply move you onto the right hr support package at the next renewal date, and we will tell you proactively rather than waiting for you to ask. " +
      "Many of our clients start on Essentials, move to Growth as they take on managers, and graduate to an Enterprise hr outsourcing package once they have multiple sites. " +
      "Because the contract is rolling, you can also pause specific add-ons (such as training days) in quieter months without losing your core cover.",
  },
  {
    question: "How do EmployerHQ HR packages for small businesses compare with Peninsula, Citation or BrightHR?",
    answer:
      "Most of the well-known hr support packages on the UK market lock small businesses into 3 or 5-year contracts with steep early-exit fees, then route advice through a large pool of generalist advisors. " +
      "EmployerHQ hr packages for small businesses are different in three concrete ways: (1) all contracts are monthly rolling with 30 days' notice; (2) you get a named senior advisor (not a call-centre queue) who handles your cases end-to-end; and (3) employment law and tribunal cover is built into the hr consulting package, not sold as a separate insurance product. " +
      "We are happy to review any existing hr retainer package or quote side-by-side so you can compare like for like on price, scope and exit terms.",
  },
  {
    question: "Are HR packages for small businesses worth it for a team of under 10?",
    answer:
      "For most UK small businesses, hr packages for small businesses become cost-effective from the very first employee, not at some arbitrary headcount threshold. " +
      "Even a team of 5 needs compliant contracts, a written statement of particulars, a basic policy suite, a process for absence and pay, and somewhere to turn when a difficult conversation arises. " +
      "Our Essentials hr package at £199 per month covers all of that, plus unlimited advice, which is usually cheaper than a single hour of employment-solicitor time. " +
      "The break-even point is not headcount, it is risk: as soon as you have one employee, the Employment Rights Act 1996 and the Equality Act 2010 apply in full, and an unfair-dismissal or discrimination claim could cost £20,000 or more to defend even if you win.",
  },
];

export const Route = createFileRoute("/hr-packages")({
  head: () =>
    buildHead({
      title: META_TITLE,
      description: META_DESCRIPTION,
      path: "/hr-packages",
      schemas: [
        serviceSchema({
          name: "HR Packages for Small Businesses",
          description:
            "Fixed-fee monthly HR support packages and employment law cover for UK small businesses, from £199 per month with a named advisor.",
          serviceType: "HR Outsourcing",
        }),
        faqSchema(ensureFAQs(PRIMARY_KEYWORD, customFaqs, "service")),
        breadcrumbSchema([
          { label: "Home", path: "/" },
          { label: "Pricing", path: "/hr-packages" },
        ]),
      ],
    }),
  component: PricingPage,
});

const plans = [
  {
    name: "Essentials",
    price: "£199",
    cadence: "per month",
    for: "Up to 25 employees",
    blurb: "Our entry-level HR package for small businesses taking their first step beyond DIY HR.",
    features: [
      "Named senior CIPD advisor",
      "Unlimited phone & email advice",
      "Bespoke contract & handbook",
      "Standard policy library",
      "Disciplinary & grievance support",
    ],
  },
  {
    name: "Growth",
    price: "£449",
    cadence: "per month",
    for: "25–100 employees",
    highlight: true,
    blurb: "Our most popular HR support package, with full employment law cover built in.",
    features: [
      "Everything in Essentials",
      "Solicitor-led legal advice",
      "Tribunal preparation & defence",
      "Manager training (2 sessions/year)",
      "Quarterly strategic reviews",
    ],
  },
  {
    name: "Enterprise",
    price: "Custom",
    cadence: "",
    for: "100+ employees",
    blurb: "A bespoke HR outsourcing package for multi-site and group structures.",
    features: [
      "Everything in Growth",
      "Multi-advisor team",
      "On-site support days",
      "Bespoke training programmes",
      "Group company support",
    ],
  },
];

function PricingPage() {
  return (
    <>
      <PageHero
        breadcrumbSchema={false}
        breadcrumbs={[
          { label: "Home", path: "/" },
          { label: "Pricing", path: "/hr-packages" },
        ]}
        eyebrow="Pricing"
        title="HR Packages for Small Businesses"
        subtitle="Transparent HR support packages and employment law cover, monthly rolling, no tie-in."
        intro="Fixed monthly fees from £199. Pick the hr package that fits your business today, scale up or down as your team grows."
      />

      <section className="px-6 py-16 lg:px-8 lg:py-20">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 lg:grid-cols-3">
          {plans.map((p) => (
            <div
              key={p.name}
              className={`flex flex-col rounded-3xl border p-8 ${
                p.highlight ? "border-accent bg-brand text-brand-foreground" : "border-border bg-card text-foreground"
              }`}
            >
              <h2 className="font-serif text-2xl">{p.name}</h2>
              <p className={`mt-2 text-sm ${p.highlight ? "text-brand-foreground/70" : "text-muted-foreground"}`}>
                {p.for}
              </p>
              <p className="mt-6 font-serif text-5xl">{p.price}</p>
              <p className={`text-sm ${p.highlight ? "text-brand-foreground/70" : "text-muted-foreground"}`}>{p.cadence}</p>
              <p className={`mt-4 text-sm ${p.highlight ? "text-brand-foreground/80" : "text-muted-foreground"}`}>
                {p.blurb}
              </p>
              <ul className="mt-6 flex-1 space-y-3 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-3">
                    <Check className="mt-0.5 size-4 shrink-0 text-accent" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/contact-hr-experts"
                className={`mt-8 inline-flex items-center justify-center rounded-md px-4 py-2.5 text-sm font-medium ${
                  p.highlight
                    ? "bg-accent text-accent-foreground hover:brightness-110"
                    : "border border-border bg-background text-foreground hover:bg-secondary"
                }`}
              >
                Get started
              </Link>
            </div>
          ))}
        </div>

        <p className="mx-auto mt-10 max-w-2xl text-center text-sm text-muted-foreground">
          Need something different? Project work and pay-as-you-go solicitor support are quoted as fixed fees. Just{" "}
          <Link to="/contact-hr-experts" className="text-accent underline-offset-4 hover:underline">
            get in touch
          </Link>
          .
        </p>
      </section>

      <section className="px-6 py-20 lg:px-8" aria-labelledby="why-packages-heading">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Why a package, not pay-as-you-go
            </span>
            <h2 id="why-packages-heading" className="mt-4 font-serif text-3xl leading-tight text-brand sm:text-4xl">
              Why UK SMEs choose HR packages for small businesses
            </h2>
            <p className="mt-5 text-muted-foreground">
              For most UK SMEs, hr packages for small businesses are the most cost-effective way to stay compliant with the Employment Rights Act 1996, the Equality Act 2010 and the ACAS Code of Practice without hiring an in-house HR manager.
              A fixed monthly hr package gives you a named senior advisor, a tested policy library and a clear escalation path the moment a difficult conversation starts.
            </p>
            <p className="mt-4 text-muted-foreground">
              Pay-as-you-go HR and employment-law work is unpredictable: solicitors charge £250–£450 per hour, and a single contested dismissal can run to £8,000–£25,000 in fees before any award is paid out.
              An hr retainer package converts that volatility into a single line on your P&amp;L and, because your advisor already knows the business, cases are resolved faster.
            </p>
          </div>

          <div>
            <h3 className="font-serif text-2xl text-brand">What every EmployerHQ hr support package includes</h3>
            <ul className="mt-6 space-y-3 text-sm text-foreground">
              {[
                "A named CIPD-qualified senior advisor, not a call-centre queue",
                "Unlimited phone and email advice on UK employment law",
                "Bespoke contract of employment and a full policy handbook",
                "Disciplinary, grievance, absence and performance case handling",
                "Annual policy refresh as UK employment law changes",
                "Monthly rolling contract with 30 days' notice, no early-exit fees",
              ].map((item) => (
                <li key={item} className="flex gap-3">
                  <Check className="mt-0.5 size-4 shrink-0 text-accent" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-muted-foreground">
              Compare any existing hr consultancy package or hr outsourcing package against ours and we will show you the difference in scope, response time and total annual cost, in writing, before you switch.
            </p>
          </div>
        </div>
      </section>

      <SeoFAQ primaryKeyword={PRIMARY_KEYWORD} faqs={customFaqs} type="service" />

      <InternalLinksHub heading="What's included with every HR package" />
      <CTASection
        headline="Not sure which HR package is right for your small business?"
        sub="A 15-minute call and we'll tell you straight, no sales script."
      />
    </>
  );
}
